// Story 056 — pure histogram math. No DOM, no worker glue: this module is
// imported by the Web Worker (`src/workers/histogram.worker.ts`) for the
// off-main-thread bucketing AND by unit tests directly. Keep it dependency-
// free so the worker bundle stays tiny.

/** One bucket per 8-bit level. */
export const HISTOGRAM_BINS = 256;

/**
 * Clipping threshold, in percent of sampled pixels (story Open Q3 —
 * decided at 1 %: 0.5 % is noisier, 2 % misses real problems). A bucket-0
 * share above this shows "Shadows clipped"; a bucket-255 share above it
 * shows "Highlights clipped". A `const` by design so a future settings
 * drawer can take ownership without an algorithm change.
 */
export const CLIPPING_THRESHOLD_PCT = 1;

export interface HistogramResult {
  /** Luminance (Rec. 709 luma of the sRGB channels), 256 buckets. */
  l: Uint32Array;
  r: Uint32Array;
  g: Uint32Array;
  b: Uint32Array;
}

export interface ClippingPercentages {
  /** Share of sampled pixels in bucket 0, in percent [0, 100]. */
  shadowsPct: number;
  /** Share of sampled pixels in bucket 255, in percent [0, 100]. */
  highlightsPct: number;
}

/**
 * Bucket an RGBA pixel buffer (the `data` of an `ImageData`) into four
 * 256-bin histograms: per-channel R/G/B plus a Rec. 709 luminance trace.
 *
 * Fully transparent pixels (alpha 0) are SKIPPED: an un-premultiplied
 * canvas hands them over as RGB 0/0/0 after compositing, so counting them
 * would pile phantom mass into bucket 0 and fake a "Shadows clipped"
 * reading. Drone imagery is opaque JPEG/WebP, so in practice this only
 * guards decoded PNGs with an alpha channel. Partially transparent
 * pixels keep their RGB contribution.
 */
export function computeHistogram(data: Uint8ClampedArray): HistogramResult {
  const l = new Uint32Array(HISTOGRAM_BINS);
  const r = new Uint32Array(HISTOGRAM_BINS);
  const g = new Uint32Array(HISTOGRAM_BINS);
  const b = new Uint32Array(HISTOGRAM_BINS);

  for (let i = 0; i + 3 < data.length; i += 4) {
    const alpha = data[i + 3] ?? 255;
    if (alpha === 0) continue;
    const red = data[i] ?? 0;
    const green = data[i + 1] ?? 0;
    const blue = data[i + 2] ?? 0;
    r[red] = (r[red] ?? 0) + 1;
    g[green] = (g[green] ?? 0) + 1;
    b[blue] = (b[blue] ?? 0) + 1;
    // Rec. 709 luma. The coefficients sum to 1, so the rounded value is
    // always within [0, 255] for 8-bit inputs — no clamp needed.
    const luma = Math.round(0.2126 * red + 0.7152 * green + 0.0722 * blue);
    l[luma] = (l[luma] ?? 0) + 1;
  }

  return { l, r, g, b };
}

/**
 * Share of sampled pixels sitting in the extreme buckets of the luminance
 * trace. An empty histogram (no pixels) reports 0 % / 0 % so the widget's
 * empty state never claims clipping.
 */
export function clippingPercentages(l: Uint32Array): ClippingPercentages {
  let total = 0;
  for (const count of l) total += count;
  if (total === 0) return { shadowsPct: 0, highlightsPct: 0 };
  return {
    shadowsPct: ((l[0] ?? 0) / total) * 100,
    highlightsPct: ((l[HISTOGRAM_BINS - 1] ?? 0) / total) * 100,
  };
}

/** Does a clipping share cross the 1 % display threshold? */
export function isClipped(pct: number): boolean {
  return pct >= CLIPPING_THRESHOLD_PCT;
}
